import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  Collapse,
  IconButton,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  FilterList as FilterIcon,
  CalendarToday as CalendarIcon,
  QrCode2 as QrCodeIcon,
  Edit as EditIcon,
  Warehouse as WarehouseIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { getActivityDisplay } from '../constants/activityTypes';
import activityService from '../services/activity.service';
import StatusHistoryDialog from './StatusHistoryDialog';
import api from '../services/api.service';
import equipmentService from '../services/equipment.service';
import { printSingleQRCode } from '../services/qrCodePrintService';

/**
 * Activity type mapping - maps backend activity types to centralized constants
 * Extracted to avoid duplication across multiple rendering locations
 */
const ACTIVITY_TYPE_MAPPING = {
  'received': 'EQUIPMENT_RECEIVED',
  'assigned': 'EQUIPMENT_ASSIGNED',
  'checked_out': 'EQUIPMENT_CHECKED_OUT',
  'returned': 'EQUIPMENT_RETURNED',
  'returned_to_job': 'EQUIPMENT_RETURNED_TO_JOB',
  'maintenance': 'EQUIPMENT_MAINTENANCE',
  'transferred': 'EQUIPMENT_TRANSFERRED',
  'installed': 'EQUIPMENT_INSTALLED',
  'archived': 'EQUIPMENT_ARCHIVED',
  'status_change': 'STATUS_CHANGED',
  'planned': 'EQUIPMENT_PLANNED',
  'plan_updated': 'EQUIPMENT_PLAN_UPDATED',
  'plan_deleted': 'EQUIPMENT_PLAN_DELETED',
  'ordered': 'EQUIPMENT_ORDERED',
  'note_added': 'NOTE_ADDED'
};

/**
 * Recent Activity Component
 * Displays a timeline of recent equipment activities
 */
export default function RecentActivity({ limit = 100, showHeader = true, autoRefresh = false }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Removed expandedGroups state - no longer using date grouping

  // Pagination state
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // Date filter state
  const [dateFilter, setDateFilter] = useState('recent'); // 'recent', 'today', 'lastWeek', 'lastMonth', 'custom'
  const [showCustomDateDialog, setShowCustomDateDialog] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Search state
  const [searchText, setSearchText] = useState('');

  // Status History dialog state
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyItem, setHistoryItem] = useState(null);

  // Ref to track the list scroll container
  const listContainerRef = useRef(null);

  // Edit functionality state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [allWarehouses, setAllWarehouses] = useState([]);
  const [allZones, setAllZones] = useState([]);
  const [editBays, setEditBays] = useState([]);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Get date range based on filter
  const getDateRange = useCallback(() => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (dateFilter) {
      case 'recent':
        // Recent = 0-6 days ago (today through 6 days ago)
        const sixDaysAgo = new Date(today.getTime() - 6 * 24 * 60 * 60 * 1000);
        return {
          startDate: sixDaysAgo,
          endDate: now
        };
      case 'today':
        return {
          startDate: today,
          endDate: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
        };
      case 'lastWeek':
        // Last Week = 7-13 days ago (full 7-day period)
        const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        const thirteenDaysAgo = new Date(today.getTime() - 13 * 24 * 60 * 60 * 1000);
        return {
          startDate: thirteenDaysAgo,
          endDate: new Date(sevenDaysAgo.getTime() + 24 * 60 * 60 * 1000 - 1) // End of day 7
        };
      case 'lastMonth':
        // Last Month = 14+ days ago (no upper limit)
        const fourteenDaysAgo = new Date(today.getTime() - 14 * 24 * 60 * 60 * 1000);
        return {
          startDate: new Date('2000-01-01'), // Far past date (no upper limit)
          endDate: new Date(fourteenDaysAgo.getTime() + 24 * 60 * 60 * 1000 - 1) // End of day 14
        };
      case 'custom':
        return {
          startDate: customStartDate ? new Date(customStartDate) : null,
          endDate: customEndDate ? new Date(customEndDate + 'T23:59:59') : null
        };
      default:
        return { startDate: null, endDate: null };
    }
  }, [dateFilter, customStartDate, customEndDate]);

  // Load activities
  const loadActivities = useCallback(async () => {
    try {
      // Save current scroll positions before loading
      const currentScrollTop = listContainerRef.current?.scrollTop || 0;
      // Also save the main page scroll position
      const mainContent = document.querySelector('main');
      const mainScrollTop = mainContent?.scrollTop || 0;

      setLoading(true);
      setError(null);
      // Reset pagination when filters change
      setOffset(0);

      const dateRange = getDateRange();
      const params = { limit, offset: 0 };

      // Add date filtering if applicable
      if (dateRange.startDate) {
        params.startDate = dateRange.startDate.toISOString();
      }
      if (dateRange.endDate) {
        params.endDate = dateRange.endDate.toISOString();
      }

      const response = await activityService.getRecentActivity(params);
      console.log('RecentActivity: Full API response:', response);

      // Handle both cases: response could be direct array or wrapped in data/data structure
      let activities;
      let meta = {};
      if (Array.isArray(response)) {
        activities = response;
      } else if (Array.isArray(response.data)) {
        activities = response.data;
        meta = response.meta || {};
      } else if (Array.isArray(response.data?.data)) {
        activities = response.data.data;
        meta = response.data.meta || {};
      } else {
        activities = [];
      }

      console.log('RecentActivity: Setting activities:', activities);
      console.log('RecentActivity: Pagination meta:', meta);
      // Log the first activity to see what fields are available
      if (activities.length > 0) {
        console.log('First activity data:', activities[0]);
        console.log('Equipment Code:', activities[0].equipmentCode);
      }
      setActivities(activities);
      setTotalCount(meta.total || activities.length);
      setHasMore(meta.hasMore || false);

      // Restore scroll positions after a brief delay to allow DOM update
      setTimeout(() => {
        if (listContainerRef.current) {
          listContainerRef.current.scrollTop = currentScrollTop;
        }
        // Restore main page scroll position
        if (mainContent) {
          mainContent.scrollTop = mainScrollTop;
        }
      }, 0);
    } catch (err) {
      console.error('Error loading recent activity:', err);
      setError('Failed to load recent activity');
    } finally {
      setLoading(false);
    }
  }, [limit, getDateRange]);

  // Load more activities (pagination)
  const loadMoreActivities = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);
      setError(null);

      const newOffset = offset + limit;
      const dateRange = getDateRange();
      const params = { limit, offset: newOffset };

      // Add date filtering if applicable
      if (dateRange.startDate) {
        params.startDate = dateRange.startDate.toISOString();
      }
      if (dateRange.endDate) {
        params.endDate = dateRange.endDate.toISOString();
      }

      const response = await activityService.getRecentActivity(params);
      console.log('RecentActivity: Load more response:', response);

      // Handle both cases: response could be direct array or wrapped in data/data structure
      let newActivities;
      let meta = {};
      if (Array.isArray(response)) {
        newActivities = response;
      } else if (Array.isArray(response.data)) {
        newActivities = response.data;
        meta = response.meta || {};
      } else if (Array.isArray(response.data?.data)) {
        newActivities = response.data.data;
        meta = response.data.meta || {};
      } else {
        newActivities = [];
      }

      // Append new activities to existing ones
      setActivities(prev => [...prev, ...newActivities]);
      setOffset(newOffset);
      setTotalCount(meta.total || totalCount);
      setHasMore(meta.hasMore || false);

    } catch (err) {
      console.error('Error loading more activities:', err);
      setError('Failed to load more activities');
    } finally {
      setLoadingMore(false);
    }
  }, [limit, offset, hasMore, loadingMore, getDateRange, totalCount]);

  // Initial load and reload when filters change
  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  // Auto-refresh setup
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(loadActivities, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [autoRefresh, loadActivities]);

  // Listen for equipment status changes (from Quick Actions or other components)
  useEffect(() => {
    const handleEquipmentChange = (event) => {
      console.log('🔄 RecentActivity received equipmentStatusChanged event:', event.detail);
      console.log('   Refreshing activity list...');
      // Trigger immediate refresh of recent activities
      loadActivities();
    };

    window.addEventListener('equipmentStatusChanged', handleEquipmentChange);
    console.log('✅ RecentActivity: Listening for equipment status changes');

    return () => {
      window.removeEventListener('equipmentStatusChanged', handleEquipmentChange);
    };
  }, [loadActivities]); // Re-attach listener if loadActivities changes

  // Load warehouses and zones for edit functionality
  useEffect(() => {
    const loadLocationData = async () => {
      try {
        // Load warehouses
        const warehouseResponse = await api.get('/inventories/warehouses');
        // Handle various response structures and ensure we have an array
        const warehouseData = warehouseResponse.data?.data?.warehouses || 
                             warehouseResponse.data?.warehouses || 
                             warehouseResponse.data?.data || 
                             warehouseResponse.data || [];
        const warehousesArray = Array.isArray(warehouseData) ? warehouseData : [];
        setAllWarehouses(warehousesArray.filter(w => w.isActive !== false));

        // Load zones/racks
        const rackResponse = await api.get('/inventories/racks');
        const rackData = rackResponse.data?.data || rackResponse.data || [];
        setAllZones(rackData);
      } catch (err) {
        console.error('Error loading location data:', err);
      }
    };
    loadLocationData();
  }, []);

  // Filter activities by search text
  const filteredActivities = activities.filter(activity => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      activity.equipment_id?.toLowerCase().includes(search) ||
      activity.job_id?.toLowerCase().includes(search) ||
      activity.job_name?.toLowerCase().includes(search) ||
      activity.description?.toLowerCase().includes(search) ||
      activity.action_type?.toLowerCase().includes(search) ||
      activity.user_name?.toLowerCase().includes(search)
    );
  });

  // Removed toggleGroup function - no longer using date grouping

  // Get activity icon based on type
  const getActivityIcon = (activity) => {
    const activityType = ACTIVITY_TYPE_MAPPING[activity.activityType] || activity.activityType || 'STATUS_CHANGED';
    const display = getActivityDisplay(activityType);
    const IconComponent = display.icon;

    return <IconComponent size={16} />;
  };

  // Handle date filter change
  const handleDateFilterChange = (newFilter) => {
    setDateFilter(newFilter);
    if (newFilter === 'custom') {
      setShowCustomDateDialog(true);
    }
  };

  // Handle custom date range selection
  const handleCustomDateSubmit = () => {
    setShowCustomDateDialog(false);
    // loadActivities will be called automatically by useEffect
  };

  // Handle custom date dialog close
  const handleCustomDateClose = () => {
    setShowCustomDateDialog(false);
    if (!customStartDate && !customEndDate) {
      // If no custom dates were set, revert to 'today'
      setDateFilter('today');
    }
  };

  // Handle QR code printing
  const handleShowQRCode = async (activity) => {
    // Create an item object for the print service
    const itemForQR = {
      id: activity.equipmentId || activity.id,
      serial_number: activity.serialNumber,
      description: activity.equipmentName,
      manufacturer: activity.manufacturer,
      model: activity.model,
      job_id: activity.jobId,
      job_name: activity.jobName,
      job_number: activity.jobNumber
    };
    await printSingleQRCode(itemForQR);
  };

  // Handle status history dialog
  const handleShowHistory = (activity) => {
    // Create a mock equipment item from activity data for status history
    const equipmentItem = {
      id: activity.equipmentId || activity.id,
      equipment_id: activity.equipmentId,
      description: activity.equipmentName,
      manufacturer: activity.manufacturer || '',
      model: activity.model || '',
      serial_number: activity.serialNumber || '',
      status: activity.status || 'available'
    };
    
    setHistoryItem(equipmentItem);
    setHistoryDialogOpen(true);
  };

  const handleHistoryClose = () => {
    setHistoryDialogOpen(false);
    setHistoryItem(null);
  };

  // Edit functionality handlers
  const handleEditItem = async (activity) => {
    try {
      // Fetch full equipment details for editing
      const response = await equipmentService.getEquipmentById(activity.equipmentId || activity.id);
      const equipmentItem = response.data;
      
      setEditingItem(equipmentItem);
      setEditFormData({
        description: equipmentItem.description || '',
        manufacturer: equipmentItem.manufacturer || '',
        model: equipmentItem.model || '',
        serial_number: equipmentItem.serial_number || '',
        warehouse: equipmentItem.warehouse_id || '',
        zone: equipmentItem.rack_id || '',
        bay: equipmentItem.bay_id || ''
      });
      
      // Load bays if rack is selected
      if (equipmentItem.warehouse_id && equipmentItem.rack_id) {
        loadEditBays(equipmentItem.warehouse_id, equipmentItem.rack_id);
      }
      
      setEditDialogOpen(true);
    } catch (error) {
      console.error('Error fetching equipment details for edit:', error);
    }
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditingItem(null);
    setEditFormData({});
    setSaveError(null);
    setEditBays([]);
  };

  const handleEditFormChange = (field) => (event) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Load bays when rack changes
    if (field === 'zone' && event.target.value && editFormData.warehouse) {
      loadEditBays(editFormData.warehouse, event.target.value);
    }
    // Clear bays when warehouse changes
    if (field === 'warehouse') {
      setEditBays([]);
      setEditFormData(prev => ({ ...prev, bay: '' }));
    }
  };
  
  // Load bays for edit dialog
  const loadEditBays = async (warehouseId, rackId) => {
    try {
      const response = await api.get(`/inventories/bays?warehouseId=${warehouseId}&rackId=${rackId}`);
      const baysData = response.data?.data || response.data || [];
      setEditBays(baysData);
    } catch (error) {
      console.error('Error loading bays:', error);
      setEditBays([]);
    }
  };

  const handleEditSave = async () => {
    try {
      setLoading(true);
      setSaveError(null);
      
      const updateData = {
        description: editFormData.description,
        manufacturer: editFormData.manufacturer,
        model: editFormData.model,
        serial_number: editFormData.serial_number,
        warehouse_id: editFormData.warehouse,
        rack_id: editFormData.zone,
        bay_id: editFormData.bay
      };
      
      await api.put(`/equipment-items/${editingItem.id}`, updateData);

      setSaveSuccess(true);
      handleEditClose();
      // Refresh activities to show updated information
      loadActivities();
    } catch (error) {
      console.error('Error updating equipment:', error);
      setSaveError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Render activity item
  const renderActivityItem = (activity) => (
    <ListItem 
      key={activity.id} 
      sx={{ 
        py: 1,
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'action.hover'
        }
      }}
      onClick={(e) => {
        // Don't trigger on button clicks
        if (e.target.closest('button')) return;
        handleShowHistory(activity);
      }}
    >
      <ListItemAvatar>
        <Avatar
          sx={{
            bgcolor: (() => {
              const activityType = ACTIVITY_TYPE_MAPPING[activity.activityType] || activity.activityType || 'STATUS_CHANGED';
              const display = getActivityDisplay(activityType);
              return display.color + '20'; // Add transparency
            })(),
            color: (() => {
              const activityType = ACTIVITY_TYPE_MAPPING[activity.activityType] || activity.activityType || 'STATUS_CHANGED';
              const display = getActivityDisplay(activityType);
              return display.color;
            })(),
            width: 32,
            height: 32
          }}
        >
          {getActivityIcon(activity)}
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" component="span">
              <strong>{activity.userName}</strong> {activity.description}
              {activity.equipmentCode && (
                <> <strong>{activity.equipmentCode}</strong></>
              )}
              {activity.location && activity.location !== 'inventory' && (
                <> at <strong>{activity.location}</strong></>
              )}
            </Typography>
          </Box>
        }
        secondary={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
            {/* Enhanced context information */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {(activity.equipmentCode || activity.equipmentId) && (
                <>
                  <Typography variant="caption" color="text.secondary">
                    ID: <strong>{activity.equipmentCode || `#${activity.equipmentId}`}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">•</Typography>
                </>
              )}
              {activity.serialNumber && (
                <>
                  <Typography variant="caption" color="text.secondary">
                    Serial: <strong>{activity.serialNumber}</strong>
                  </Typography>
                  <Typography variant="caption" color="text.secondary">•</Typography>
                </>
              )}
              {activity.jobId && (
                <>
                  <Typography variant="caption" color="text.secondary">
                    Job: {activity.jobName || activity.job_name || `#${activity.jobId}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">•</Typography>
                </>
              )}
              {activity.warehouseName && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <WarehouseIcon sx={{ fontSize: 12 }} />
                    <Typography variant="caption" color="text.secondary">
                      {activity.warehouseName}
                    </Typography>
                  </Box>
                  {(activity.rackName || activity.bayName) && (
                    <Typography variant="caption" color="text.secondary">•</Typography>
                  )}
                </>
              )}
              {activity.rackName && (
                <Typography variant="caption" color="text.secondary">
                  Rack {activity.rackName}
                </Typography>
              )}
              {activity.bayName && (
                <Typography variant="caption" color="text.secondary">
                  Bay {activity.bayName}
                </Typography>
              )}
              {activity.category && (
                <>
                  <Typography variant="caption" color="text.secondary">•</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CategoryIcon sx={{ fontSize: 12 }} />
                    <Typography variant="caption" color="text.secondary">
                      {activity.category}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
            
            {/* Time and user info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {activityService.formatActivityTime(activity.timestamp)}
              </Typography>
              {activity.userName && activity.userName !== 'System' && (
                <>
                  <Typography variant="caption" color="text.secondary">•</Typography>
                  <Typography variant="caption" color="text.secondary">
                    by {activity.userName}
                  </Typography>
                </>
              )}
              {activity.notes && activity.notes.length > 0 && (
                <Chip 
                  label="Note" 
                  size="small" 
                  variant="outlined" 
                  sx={{ height: 16, fontSize: '0.625rem' }}
                />
              )}
            </Box>
          </Box>
        }
      />
      
      {/* Action buttons */}
      {(activity.equipmentId || activity.id) && (
        <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              handleEditItem(activity);
            }}
            sx={{ color: 'primary.main' }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton 
            size="small" 
            onClick={(e) => {
              e.stopPropagation();
              handleShowQRCode(activity);
            }}
          >
            <QrCodeIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </ListItem>
  );

  // Render activity group
  // Removed renderActivityGroup function - no longer using date grouping

  if (loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Loading recent activity...
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error" action={
          <IconButton size="small" onClick={loadActivities}>
            <RefreshIcon />
          </IconButton>
        }>
          {error}
        </Alert>
      </Paper>
    );
  }

  if (activities.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography variant="h6" color="text.secondary">
          No Recent Activity
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Equipment activity will appear here as it happens
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      {showHeader && (
        <>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">
              Recent Activity
            </Typography>
            <IconButton size="small" onClick={loadActivities} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Box>
          
          {/* Date Filter Controls */}
          <Box sx={{ px: 2, pb: 2 }}>
            <ToggleButtonGroup
              value={dateFilter}
              exclusive
              onChange={(event, newFilter) => {
                if (newFilter !== null) {
                  handleDateFilterChange(newFilter);
                }
              }}
              size="small"
              sx={{ width: '100%' }}
            >
              <ToggleButton value="recent" sx={{ flex: 1 }}>
                Recent
              </ToggleButton>
              <ToggleButton value="lastWeek" sx={{ flex: 1 }}>
                Last Week
              </ToggleButton>
              <ToggleButton value="lastMonth" sx={{ flex: 1 }}>
                Last Month
              </ToggleButton>
              <ToggleButton value="custom" sx={{ flex: 1 }}>
                <CalendarIcon fontSize="small" sx={{ mr: 0.5 }} />
                Custom
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Search Bar */}
          <Box sx={{ px: 2, pb: 2 }}>
            <TextField
              placeholder="Search by equipment ID, job, description, action, or user..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              size="small"
              fullWidth
            />
          </Box>
        </>
      )}
      
      <List
        ref={listContainerRef}
        disablePadding
        sx={{ maxHeight: 400, overflow: 'auto' }}
      >
        {/* Show activities directly without date grouping */}
        {filteredActivities.map(renderActivityItem)}
      </List>

      {/* Pagination controls */}
      {filteredActivities.length > 0 && (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, borderTop: 1, borderColor: 'divider' }}>
          {/* Count display */}
          <Typography variant="body2" color="text.secondary">
            Showing {filteredActivities.length} of {totalCount} activities
          </Typography>

          {/* Load More button */}
          {hasMore && (
            <Button
              variant="outlined"
              onClick={loadMoreActivities}
              disabled={loadingMore}
              startIcon={loadingMore ? <CircularProgress size={16} /> : null}
              fullWidth
            >
              {loadingMore ? 'Loading...' : 'Load More'}
            </Button>
          )}
        </Box>
      )}

      {/* Custom Date Range Dialog */}
      <Dialog open={showCustomDateDialog} onClose={handleCustomDateClose} maxWidth="xs" fullWidth>
        <DialogTitle>Custom Date Range</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Start Date"
              type="date"
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="End Date"
              type="date"
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCustomDateClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleCustomDateSubmit} 
            variant="contained"
            disabled={!customStartDate && !customEndDate}
          >
            Apply Filter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status History Dialog */}
      <StatusHistoryDialog
        open={historyDialogOpen}
        onClose={handleHistoryClose}
        equipment={historyItem}
      />

      {/* Equipment Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit Equipment: {editingItem?.equipment_id || editingItem?.id}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {/* Read-only fields */}
            <TextField
              label="Equipment ID"
              value={editingItem?.equipment_id || editingItem?.id || ''}
              fullWidth
              disabled
              variant="outlined"
              size="small"
            />

            {/* Editable fields */}
            <TextField
              label="Description"
              value={editFormData.description || ''}
              onChange={handleEditFormChange('description')}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Enter equipment description"
            />
            <TextField
              label="Manufacturer"
              value={editFormData.manufacturer || ''}
              onChange={handleEditFormChange('manufacturer')}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Enter manufacturer"
            />
            <TextField
              label="Model"
              value={editFormData.model || ''}
              onChange={handleEditFormChange('model')}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Enter model"
            />
            <TextField
              label="Serial Number"
              value={editFormData.serial_number || ''}
              onChange={handleEditFormChange('serial_number')}
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Enter serial number"
            />

            {/* Location fields - dropdowns */}
            <FormControl fullWidth size="small">
              <InputLabel>Warehouse</InputLabel>
              <Select
                value={editFormData.warehouse || ''}
                onChange={handleEditFormChange('warehouse')}
                label="Warehouse"
                disabled={allWarehouses.length === 0}
              >
                <MenuItem value="">
                  <em>Not assigned</em>
                </MenuItem>
                {allWarehouses.length === 0 ? (
                  <MenuItem disabled>
                    <em>Loading warehouses...</em>
                  </MenuItem>
                ) : (
                  allWarehouses.map((warehouse) => (
                    <MenuItem key={warehouse.id} value={warehouse.id}>
                      {warehouse.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Rack</InputLabel>
              <Select
                value={editFormData.zone || ''}
                onChange={handleEditFormChange('zone')}
                label="Rack"
                disabled={!editFormData.warehouse || allZones.length === 0}
              >
                <MenuItem value="">
                  <em>Not assigned</em>
                </MenuItem>
                {allZones.length === 0 ? (
                  <MenuItem disabled>
                    <em>Loading racks...</em>
                  </MenuItem>
                ) : (
                  allZones
                    .filter(zone => !editFormData.warehouse || zone.warehouse_id === editFormData.warehouse)
                    .map((zone) => (
                      <MenuItem key={zone.id} value={zone.id}>
                        {zone.name}
                      </MenuItem>
                    ))
                )}
              </Select>
            </FormControl>

            <FormControl fullWidth size="small">
              <InputLabel>Bay</InputLabel>
              <Select
                value={editFormData.bay || ''}
                onChange={handleEditFormChange('bay')}
                label="Bay"
                disabled={!editFormData.zone || editBays.length === 0}
              >
                <MenuItem value="">
                  <em>Not assigned</em>
                </MenuItem>
                {editBays.length === 0 && editFormData.zone ? (
                  <MenuItem disabled>
                    <em>Loading bays...</em>
                  </MenuItem>
                ) : (
                  editBays.map((bay) => (
                    <MenuItem key={bay.id} value={bay.id}>
                      {bay.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {saveError && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {saveError}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleEditSave} 
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={saveSuccess}
        autoHideDuration={6000}
        onClose={() => setSaveSuccess(false)}
        message="Equipment updated successfully!"
      />
    </Paper>
  );
}